import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatDateTime } from '../lib/format'
import type { Customer, DokuErgebnis, DokuStand } from '../lib/types'
import { ErrorBox, Loading } from './Feedback'

/**
 * Export und Doku-Plattform im Inventar (Stufe 4.5): das Inventar als CSV für
 * Excel, und je Kunde die Firma in Hudu bzw. IT Glue, in die die Geräte
 * geschrieben werden.
 */
export function InventarExport({ customer }: { customer?: Customer }) {
  const { canForCustomer } = useAuth()
  const href = `/api/inventar.csv${customer ? `?kunde=${customer.id}` : ''}`
  return (
    <section className="card">
      <div className="card-body row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong>Export</strong>
        <a className="btn btn-sm" href={href} download>
          Als CSV herunterladen
        </a>
        <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 240 }}>
          Für Excel: Semikolon getrennt, UTF-8. Enthält Kunde, Standort, Modell, Firmware, Seriennummer, IP-Adresse und
          den Lebenszyklus; keine Zugangsdaten.
        </span>
      </div>
      {customer && canForCustomer(customer.id, 'settings.manage') ? <DokuZuordnung customer={customer} /> : null}
    </section>
  )
}

function DokuZuordnung({ customer }: { customer: Customer }) {
  const queryClient = useQueryClient()
  const jetzt = customer.doku_firma_id ? String(customer.doku_firma_id) : ''
  const [firma, setFirma] = useState(jetzt)
  const [ergebnis, setErgebnis] = useState<DokuErgebnis | null>(null)
  const speichern = useMutation({
    mutationFn: () =>
      apiSend<Customer>('PUT', `/customers/${customer.id}/doku`, { firma_id: firma.trim() ? Number(firma) : null }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['customers'] }),
  })
  const abgleich = useMutation({
    mutationFn: () => apiSend<DokuErgebnis>('POST', `/customers/${customer.id}/doku/abgleich`),
    onSuccess: (r) => {
      setErgebnis(r)
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
  })
  const gueltig = firma.trim() === '' || /^[1-9][0-9]*$/.test(firma.trim())

  return (
    <form
      className="card-body row"
      style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--border)' }}
      onSubmit={(e) => {
        e.preventDefault()
        speichern.mutate()
      }}
    >
      <div className="field">
        <label htmlFor="doku-firma">Firma in Hudu bzw. Organisation in IT Glue (Nummer)</label>
        <input
          id="doku-firma"
          className="input"
          inputMode="numeric"
          placeholder="leer = nicht abgleichen"
          value={firma}
          onChange={(e) => setFirma(e.target.value)}
        />
      </div>
      <button type="submit" className="btn btn-sm" disabled={!gueltig || firma.trim() === jetzt || speichern.isPending}>
        Zuordnung speichern
      </button>
      <button
        type="button"
        className="btn btn-sm"
        disabled={!customer.doku_firma_id || abgleich.isPending}
        onClick={() => abgleich.mutate()}
      >
        Jetzt abgleichen
      </button>
      <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 240 }}>
        {ergebnis
          ? `${ergebnis.angelegt} angelegt, ${ergebnis.aktualisiert} aktualisiert, ${ergebnis.fehlgeschlagen} fehlgeschlagen.`
          : customer.doku_abgleich_am
            ? `Zuletzt abgeglichen am ${formatDateTime(customer.doku_abgleich_am)}. Automatisch alle sechs Stunden.`
            : 'Die Geräte werden alle sechs Stunden geschrieben; nur in eine Richtung.'}
      </span>
      {(ergebnis?.fehler ?? customer.doku_fehler) ? (
        <span style={{ color: 'var(--crit)', fontSize: 13, flexBasis: '100%' }}>
          {ergebnis?.fehler ?? customer.doku_fehler}
        </span>
      ) : null}
      {speichern.isError || abgleich.isError ? (
        <span style={{ color: 'var(--crit)', fontSize: 13, flexBasis: '100%' }}>
          {((speichern.error ?? abgleich.error) as Error).message}
        </span>
      ) : null}
    </form>
  )
}

/** Einstellungen: die Verbindung zu Hudu oder IT Glue. */
export function DokuPlattformTab() {
  const queryClient = useQueryClient()
  const stand = useQuery({ queryKey: ['doku'], queryFn: ({ signal }) => apiGet<DokuStand>('/doku', signal) })
  if (stand.isPending) return <Loading what="Die Doku-Plattform" />
  if (stand.isError) return <ErrorBox error={stand.error} what="Die Doku-Plattform" />
  return (
    <DokuFormular
      key={stand.data.verbindung?.geaendert_am ?? 'neu'}
      stand={stand.data}
      onGespeichert={() => void queryClient.invalidateQueries({ queryKey: ['doku'] })}
    />
  )
}

function DokuFormular({ stand, onGespeichert }: { stand: DokuStand; onGespeichert: () => void }) {
  const v = stand.verbindung
  const [art, setArt] = useState<'hudu' | 'itglue'>(v?.art ?? 'hudu')
  const [adresse, setAdresse] = useState(v?.adresse ?? '')
  const [typ, setTyp] = useState(v ? String(v.typ_id) : '')
  const [schluessel, setSchluessel] = useState('')
  const [aktiv, setAktiv] = useState(v?.aktiv ?? true)
  const speichern = useMutation({
    mutationFn: () => apiSend('PUT', '/doku', { art, adresse, typ_id: Number(typ), schluessel, aktiv }),
    onSuccess: () => {
      setSchluessel('')
      onGespeichert()
    },
  })
  const entfernen = useMutation({ mutationFn: () => apiSend('DELETE', '/doku'), onSuccess: onGespeichert })
  const itglue = art === 'itglue'

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Doku-Plattform</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          {v ? (v.aktiv ? 'eingerichtet' : 'eingerichtet, abgeschaltet') : 'nicht eingerichtet'}
        </span>
      </div>
      <div className="card-body muted" style={{ fontSize: 13, paddingTop: 0 }}>
        Die Netzwarte schreibt die Geräte der Kunden, denen eine Firma zugeordnet ist (Berichte → Inventar), alle sechs
        Stunden in die Doku-Plattform: Name, Hersteller, Modell, Seriennummer, IP-Adresse, Firmware, Standort und
        Garantie. Zugangsdaten und Konfigurationen verlassen die Netzwarte nicht.
        {itglue
          ? ' In IT Glue entsteht je Gerät eine Konfiguration des gewählten Typs.'
          : ` In Hudu entsteht je Gerät ein Asset; das Asset-Layout braucht die Felder ${stand.hudu_felder
              .map((f) => `„${f}“`)
              .join(', ')}.`}
      </div>
      <form
        className="card-body row"
        style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault()
          speichern.mutate()
        }}
      >
        <div className="field">
          <label htmlFor="doku-art">Plattform</label>
          <select
            id="doku-art"
            className="input"
            value={art}
            onChange={(e) => {
              const neu = e.target.value as 'hudu' | 'itglue'
              setArt(neu)
              if (neu === 'itglue' && !stand.itglue_regionen.includes(adresse))
                setAdresse(stand.itglue_regionen[0] ?? '')
            }}
          >
            <option value="hudu">Hudu</option>
            <option value="itglue">IT Glue</option>
          </select>
        </div>
        <div className="field grow">
          <label htmlFor="doku-adresse">{itglue ? 'Region' : 'Adresse der Hudu-Instanz'}</label>
          {itglue ? (
            <select id="doku-adresse" className="input" value={adresse} onChange={(e) => setAdresse(e.target.value)}>
              {stand.itglue_regionen.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="doku-adresse"
              className="input"
              placeholder="https://doku.example.com"
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
            />
          )}
        </div>
        <div className="field">
          <label htmlFor="doku-typ">{itglue ? 'Konfigurationstyp (Nummer)' : 'Asset-Layout (Nummer)'}</label>
          <input
            id="doku-typ"
            className="input"
            inputMode="numeric"
            value={typ}
            onChange={(e) => setTyp(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="doku-schluessel">API-Schlüssel</label>
          <input
            id="doku-schluessel"
            className="input"
            type="password"
            autoComplete="off"
            placeholder={v?.schluessel_gesetzt && v.art === art ? 'gespeichert, leer lassen' : ''}
            value={schluessel}
            onChange={(e) => setSchluessel(e.target.value)}
          />
        </div>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
          Abgleich aktiv
        </label>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={speichern.isPending || !adresse.trim() || !/^[1-9][0-9]*$/.test(typ.trim())}
        >
          Verbindung speichern
        </button>
        {v ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={entfernen.isPending}
            onClick={() => {
              if (
                window.confirm('Die Verbindung zur Doku-Plattform entfernen? In der Plattform bleibt alles stehen.')
              ) {
                entfernen.mutate()
              }
            }}
          >
            Verbindung entfernen
          </button>
        ) : null}
        {speichern.isError || entfernen.isError ? (
          <span style={{ color: 'var(--crit)', flexBasis: '100%' }}>
            {((speichern.error ?? entfernen.error) as Error).message}
          </span>
        ) : null}
      </form>
    </section>
  )
}
